/**
 * Canonical VAT/moms helpers for billable items.
 *
 * Items store `vat_rate_percent` (0 = uden moms, >0 = med moms).
 * The percentage applied on new invoice lines comes from clinic billing
 * (`vat_rate_percent` / `default_vat_rate_percent`), not a hardcoded 25.
 */

export const FALLBACK_CLINIC_VAT_RATE = 25;

export type ClinicVatSource = {
  vat_rate_percent?: number | null;
  default_vat_rate_percent?: number | null;
};

export function clinicVatRatePercent(clinic: ClinicVatSource | null | undefined): number {
  const billing = clinic?.vat_rate_percent;
  if (typeof billing === 'number' && Number.isFinite(billing) && billing >= 0) {
    return billing;
  }
  const invoiceDefault = clinic?.default_vat_rate_percent;
  if (
    typeof invoiceDefault === 'number' &&
    Number.isFinite(invoiceDefault) &&
    invoiceDefault >= 0
  ) {
    return invoiceDefault;
  }
  return FALLBACK_CLINIC_VAT_RATE;
}

export function itemIncludesVat(vatRatePercent: number | null | undefined): boolean {
  return (vatRatePercent ?? 0) > 0;
}

export function itemVatRatePercent(
  includesVat: boolean,
  clinic: ClinicVatSource | number | null | undefined,
): number {
  if (!includesVat) return 0;
  const clinicRate = typeof clinic === 'number' ? clinic : clinicVatRatePercent(clinic);
  return clinicRate;
}

export function effectiveItemVatRate(
  storedVatRatePercent: number | null | undefined,
  clinic: ClinicVatSource | null | undefined,
): number {
  return itemVatRatePercent(itemIncludesVat(storedVatRatePercent), clinic);
}

/**
 * Same rounding as private.compute_line_totals.
 * Service price_cents is excluding moms. Moms is added on top.
 */
export function lineTotalIncludingVatCents(
  unitPriceCents: number,
  vatRatePercent: number | null | undefined,
  quantity = 1,
): number {
  const rate =
    typeof vatRatePercent === 'number' && Number.isFinite(vatRatePercent) ? vatRatePercent : 0;
  const subtotal = Math.round(quantity * Math.max(0, unitPriceCents));
  const vat = Math.round((subtotal * rate) / 100);
  return subtotal + vat;
}

/**
 * Amount to register from the appointment payment window.
 * An existing invoice total already includes moms. Otherwise the service
 * price is grossed up with the service VAT rate. A deposit stays as stored.
 */
export function appointmentPaymentAmountCents(input: {
  invoiceTotalCents?: number | null;
  invoicePaidCents?: number | null;
  netPriceCents?: number | null;
  vatRatePercent?: number | null;
  depositAmountCents?: number | null;
}): number {
  if (typeof input.invoiceTotalCents === 'number') {
    return Math.max(0, input.invoiceTotalCents - (input.invoicePaidCents ?? 0));
  }
  const deposit = input.depositAmountCents ?? 0;
  if (deposit > 0) return deposit;
  return lineTotalIncludingVatCents(input.netPriceCents ?? 0, input.vatRatePercent);
}
